import api from "./api.ts";
import type { Cart as CartType, CartUpdateParams } from "./types";

type CreateCartParams = {
  context: {
    device_id?: string;
    session_id?: number;
    ga_id?: string | null;
    initial_utm?: Record<string, string> | null;
    current_utm?: Record<string, string> | null;
    affiliate_click_id: string | null;
    affiliate_click_key: string | null;
  };
  region_id?: string;
  country_code?: string;
};

class Cart {
  private _cart_id_key = "tng_cart_id";
  private _cartData: CartType | undefined;
  private _cartHash: string | undefined;
  private _initialized = false;

  get id() {
    return this._cartData?.id;
  }
  get items() {
    return this._cartData?.items;
  }
  get original() {
    return this._cartData;
  }

  get hash() {
    return this._cartHash;
  }

  get checkoutParams() {
    const params: [string, string][] = [["cart_id", this.id!]];
    if (this._cartHash) {
      params.push(["hash", this._cartHash]);
    }
    return params;
  }

  async initialize() {
    const cartId = localStorage.getItem(this._cart_id_key);

    if (cartId) {
      await this.loadCartData();
    } else {
      await this.createCart();
    }
  }

  isInitialized(): boolean {
    return this._initialized;
  }

  async createCart(params: CreateCartParams) {
    const { cart, hash } = await api.createCart(params);

    if (cart?.id) {
      this._cartData = cart;
      this._cartHash = hash;
      this._initialized = true;
      return cart?.id;
    }

    throw new Error("Couldn't create a new Cart");
  }

  async loadCartData() {
    const cartId = localStorage.getItem(this._cart_id_key);

    if (cartId) {
      try {
        const cart = await api.loadCartData(cartId);
        if (cart.completed_at) throw new Error("Cart is completed");

        this._cartData = cart;
        this._initialized = true;
        return this._cartData;
      } catch (e) {
        return await this.createCart();
      }
    }

    throw new Error("Couldn't load a cart data");
  }

  async removeItem(productId: string) {
    if (!this._initialized) throw new Error("Cart isn't initialized");
    if (this._cartData) {
      this._cartData = await api.removeLineItem(this._cartData.id, productId);
      return this._cartData;
    }

    throw new Error("Don't have active cart");
  }

  async addItem(productId: string, quantity: number) {
    if (!this._initialized) throw new Error("Cart isn't initialized");
    const { cart } = await api.addLineItem(this._cartData!.id, {
      variant_id: productId,
      quantity: quantity,
    });
    this._cartData = cart;

    return this._cartData;
  }

  async updateCart(newData: CartUpdateParams) {
    if (this.id) {
      return await api.updateCart(this.id, newData);
    }
  }

  async applyDiscounts(discountCodes: string[]) {
    if (this.id) {
      return await api.applyMultipleDiscounts(this.id, discountCodes);
    }
  }
}

export default Cart;
